// src/utils/participantWriters.js
//
// Pass 85 — canonical participant identity WRITERS (Firestore). Every write is org-scoped and authorized against
// REAL org membership (operators create/link; a participant only ever links THEIR OWN Auth account via an
// email-verified invite). No name/email/DOB inference: identity + relationships are created by EXPLICIT action.

import { doc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { PLATFORM_COLLECTIONS } from './platformSchema';
import { canOperateOrganization } from './platformAccess';
import {
  stableParticipantId, buildParticipantProfile, assertSafeParticipantFields, PARTICIPANT_SAFE_FIELDS,
  clientParticipantLinkId, buildClientParticipantLink, lessonParticipantLinkId, buildLessonParticipantLink,
  buildParticipantInvite, normalizeParticipantEmail, PARTICIPANT_STATUS,
} from './participantIdentity';

const P = PLATFORM_COLLECTIONS.PARTICIPANT_PROFILES;
const CPL = PLATFORM_COLLECTIONS.CLIENT_PARTICIPANT_LINKS;
const LPL = PLATFORM_COLLECTIONS.LESSON_PARTICIPANT_LINKS;
const INV = PLATFORM_COLLECTIONS.PARTICIPANT_INVITES;
const LESSONS = 'lessonInstances';
const str = (v) => String(v == null ? '' : v).trim();
const fail = (code, msg) => { const e = new Error(msg); e.code = code; return e; };

// Operators (owner/admin/ops) manage participant identity + relationships. Never trust a client-supplied role.
function assertOperator(ctx, org) {
  if (!org) throw fail('PARTICIPANT_ORG_REQUIRED', 'Organisation is required.');
  if (!(ctx.userData && canOperateOrganization(ctx.userData, org))) {
    throw fail('PARTICIPANT_DENIED', 'You do not have permission to manage participants for this organisation.');
  }
}

// An opaque, non-PII creation seed. Random per creation so participantId never derives from a name/email/DOB.
function newCreationSeed() {
  return `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
}

// createParticipantProfile(db, { organizationId, displayName, preferredName?, primaryDiscipline?, goals?, ...ctx })
//   • Operator-only. Mints an opaque participantId; authUserId is ALWAYS '' at create (linked later via invite claim).
export async function createParticipantProfile(db, { organizationId = '', creationSeed = '', ...fields } = {}) {
  const org = str(organizationId);
  assertOperator(fields, org);
  assertSafeParticipantFields(fields); // reject any commercial/CRM/private field on the raw input (not silently drop it)
  const profileFields = { displayName: fields.displayName, preferredName: fields.preferredName, primaryDiscipline: fields.primaryDiscipline, goals: fields.goals };
  const seed = str(creationSeed) || newCreationSeed();
  const participantId = stableParticipantId(org, seed);
  if (!participantId) throw fail('PARTICIPANT_ID_FAILED', 'Could not mint a participant id.');
  const payload = buildParticipantProfile({ ...profileFields, participantId, organizationId: org, authUserId: '', status: PARTICIPANT_STATUS.ACTIVE, createdByAuthUserId: fields.actorAuthUserId });
  await runTransaction(db, async (tx) => {
    const ref = doc(db, P, participantId);
    const prior = await tx.get(ref);
    if (prior.exists && prior.exists()) return; // idempotent on the deterministic id — never overwrite
    tx.set(ref, { ...payload, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
  });
  return { participantId, created: true };
}

// updateParticipantProfile — operator edits SAFE coaching fields only, OR unlinks the Auth account (authUserId → '').
// It can NEVER set authUserId to a specific non-empty uid (that only happens via the participant's own email claim),
// and NEVER changes participantId/organizationId.
export async function updateParticipantProfile(db, { participantId = '', organizationId = '', unlinkAuth = false, ...fields } = {}) {
  const org = str(organizationId); const pid = str(participantId);
  assertOperator(fields, org);
  if (!pid) throw fail('PARTICIPANT_ID_REQUIRED', 'A participant id is required.');
  assertSafeParticipantFields(fields); // reject any commercial/CRM/private field before building the patch
  const patch = {};
  for (const k of PARTICIPANT_SAFE_FIELDS) if (k in fields) patch[k] = str(fields[k]);
  await runTransaction(db, async (tx) => {
    const ref = doc(db, P, pid);
    const snap = await tx.get(ref);
    if (!(snap.exists && snap.exists())) throw fail('PARTICIPANT_NOT_FOUND', 'Participant not found.');
    const cur = snap.data();
    if (str(cur.organizationId) !== org) throw fail('PARTICIPANT_DENIED', 'Cross-organisation write denied.');
    // Pass 85F.3 — a REAL unlink (a currently-linked account → '') advances the account-authorization generation, which
    // invalidates every invite issued under the old epoch (incl. pending siblings). A no-op unlink (already '') does NOT.
    const curGen = Number.isInteger(+cur.accountLinkGeneration) ? Math.floor(+cur.accountLinkGeneration) : 0;
    const realUnlink = unlinkAuth && str(cur.authUserId) !== '';
    tx.set(ref, {
      ...patch,
      ...(unlinkAuth ? { authUserId: '' } : {}),
      ...(realUnlink ? { accountLinkGeneration: curGen + 1 } : {}),
      updatedAt: serverTimestamp(),
    }, { merge: true });
  });
  return { participantId: pid, updated: true };
}

// linkClientParticipant — explicit operator link (deterministic id; idempotent). Pass 85F.1: the referenced PARTICIPANT
// must EXIST in the SAME org (no orphan / cross-org link). A link grants NO commercial/payer/guardian access.
export async function linkClientParticipant(db, { organizationId = '', clientId = '', participantId = '', provenance = '', ...ctx } = {}) {
  const org = str(organizationId);
  assertOperator(ctx, org);
  const id = clientParticipantLinkId(org, clientId, participantId);
  if (!id) throw fail('LINK_INVALID', 'organizationId, clientId and participantId are all required.');
  const payload = buildClientParticipantLink({ organizationId: org, clientId, participantId, provenance, createdByAuthUserId: ctx.actorAuthUserId });
  await runTransaction(db, async (tx) => {
    const pSnap = await tx.get(doc(db, P, str(participantId)));
    if (!(pSnap.exists && pSnap.exists())) throw fail('PARTICIPANT_NOT_FOUND', 'That participant does not exist.');
    if (str(pSnap.data().organizationId) !== org) throw fail('PARTICIPANT_WRONG_ORG', 'That participant belongs to another organisation.');
    const ref = doc(db, CPL, id);
    const prior = await tx.get(ref);
    if (prior.exists && prior.exists()) return; // idempotent — the same link can never duplicate
    tx.set(ref, { ...payload, createdAt: serverTimestamp() });
  });
  return { linkId: id, created: true };
}

// linkLessonParticipant — explicit link of a participant to a canonical lessonInstance (ONE lesson, MANY links).
// Pass 85F.1: BOTH parents (participant profile + lessonInstance) must EXIST in the SAME org. Never duplicates the
// lesson; never inferred from a clientId match.
export async function linkLessonParticipant(db, { organizationId = '', lessonId = '', participantId = '', clientId = '', provenance = '', ...ctx } = {}) {
  const org = str(organizationId);
  assertOperator(ctx, org);
  const id = lessonParticipantLinkId(org, lessonId, participantId);
  if (!id) throw fail('LINK_INVALID', 'organizationId, lessonId and participantId are all required.');
  const payload = buildLessonParticipantLink({ organizationId: org, lessonId, participantId, clientId, provenance, createdByAuthUserId: ctx.actorAuthUserId });
  await runTransaction(db, async (tx) => {
    const pSnap = await tx.get(doc(db, P, str(participantId)));
    if (!(pSnap.exists && pSnap.exists())) throw fail('PARTICIPANT_NOT_FOUND', 'That participant does not exist.');
    if (str(pSnap.data().organizationId) !== org) throw fail('PARTICIPANT_WRONG_ORG', 'That participant belongs to another organisation.');
    const lSnap = await tx.get(doc(db, LESSONS, str(lessonId)));
    if (!(lSnap.exists && lSnap.exists())) throw fail('LESSON_NOT_FOUND', 'That lesson does not exist.');
    const lOrg = str(lSnap.data().organizationId) || str(lSnap.data().orgId);
    if (lOrg !== org) throw fail('LESSON_WRONG_ORG', 'That lesson belongs to another organisation.');
    const ref = doc(db, LPL, id);
    const prior = await tx.get(ref);
    if (prior.exists && prior.exists()) return;
    tx.set(ref, { ...payload, createdAt: serverTimestamp() });
  });
  return { linkId: id, created: true };
}

// createParticipantInvite — operator prepares a targeted, email-verified account invitation. The invite may target ONLY a
// participant that EXISTS in the SAME org (Pass 85F.1). Deterministic id `org::participantId::emailNormalized`. Pass 85F.3
// makes the behavior GENERATION-aware:
//   • no existing invite → CREATE pending at the participant's CURRENT generation.
//   • existing PENDING invite at the CURRENT generation → IDEMPOTENT: return it unchanged AND return its STORED token
//     (Fix C — never a freshly-generated, unused token).
//   • existing invite that is accepted/declined OR PENDING-but-STALE (old generation) → REISSUE: ONLY while the participant
//     is UNLINKED — reset to pending, fresh token, cleared accepted actor, generation → CURRENT. Refused while linked.
export async function createParticipantInvite(db, { organizationId = '', participantId = '', email = '', token = '', ...ctx } = {}) {
  const org = str(organizationId);
  assertOperator(ctx, org);
  const pid = str(participantId); const em = normalizeParticipantEmail(email); const tok = str(token) || newCreationSeed();
  if (!pid || !em) throw fail('INVITE_INVALID', 'participantId and email are required.');
  const inviteId = `${org}::${pid}::${em}`; // deterministic so the email-verified claim can locate it in rules (no query)
  const iRef = doc(db, INV, inviteId);
  let outcome = 'created'; let returnedToken = tok;
  await runTransaction(db, async (tx) => {
    const pSnap = await tx.get(doc(db, P, pid));
    const iSnap = await tx.get(iRef);
    if (!(pSnap.exists && pSnap.exists())) throw fail('PARTICIPANT_NOT_FOUND', 'That participant does not exist.');
    const pd = pSnap.data();
    if (str(pd.organizationId) !== org) throw fail('PARTICIPANT_WRONG_ORG', 'That participant belongs to another organisation.');
    const gen = Number.isInteger(+pd.accountLinkGeneration) ? Math.floor(+pd.accountLinkGeneration) : 0;
    if (iSnap.exists && iSnap.exists()) {
      const iv = iSnap.data();
      const ivGen = Number.isInteger(+iv.accountLinkGeneration) ? Math.floor(+iv.accountLinkGeneration) : 0;
      if (str(iv.status) === 'pending' && ivGen === gen) {
        outcome = 'existing'; returnedToken = str(iv.token); return; // idempotent — return the ACTUAL stored token, no mutation
      }
      // accepted/declined OR stale-generation pending → a deliberate REISSUE, allowed ONLY while the participant is unlinked.
      if (str(pd.authUserId)) throw fail('INVITE_PARTICIPANT_LINKED', 'Unlink the existing account before re-inviting this email.');
      tx.set(iRef, { status: 'pending', token: tok, acceptedByAuthUserId: '', accountLinkGeneration: gen, createdByAuthUserId: str(ctx.actorAuthUserId), updatedAt: serverTimestamp() }, { merge: true });
      outcome = 'reissued';
      return;
    }
    const payload = buildParticipantInvite({ organizationId: org, participantId: pid, email: em, token: tok, accountLinkGeneration: gen, createdByAuthUserId: ctx.actorAuthUserId });
    tx.set(iRef, { ...payload, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
  });
  return { inviteId, token: returnedToken, outcome, created: outcome !== 'existing' };
}

// claimParticipantAccount — the participant links THEIR OWN current Auth uid to the profile AND ATOMICALLY consumes the
// matching invite (pending → accepted) in ONE transaction. Valid only when the profile is unlinked and a PENDING invite
// exists for (org, participantId, caller's verified email). An already-consumed (accepted) invite can never re-authorize
// a claim after an unlink — a relink needs a fresh operator-issued pending invite. participantId is unchanged.
//
// Pass 85F.2 — `actorEmail` / `actorEmailVerified` are UX/lookup inputs only (a caller passes `currentUser.email` /
// `currentUser.emailVerified` for an early, friendly failure); the ACTUAL security boundary is the Firestore Rules, which
// require `request.auth.token.email_verified == true` matching the invite email. This writer never trusts the boolean as truth.
export async function claimParticipantAccount(db, { participantId = '', organizationId = '', actorAuthUserId = '', actorEmail = '', actorEmailVerified = undefined } = {}) {
  const org = str(organizationId); const pid = str(participantId); const uid = str(actorAuthUserId); const em = normalizeParticipantEmail(actorEmail);
  if (!org || !pid || !uid) throw fail('CLAIM_INVALID', 'participantId, organizationId and an authenticated user are required.');
  if (!em) throw fail('CLAIM_EMAIL_REQUIRED', 'A verified email is required to claim a participant account.');
  if (actorEmailVerified === false) throw fail('CLAIM_EMAIL_UNVERIFIED', 'Verify your email address before claiming this participant account.');
  const inviteId = `${org}::${pid}::${em}`;
  await runTransaction(db, async (tx) => {
    const pRef = doc(db, P, pid); const iRef = doc(db, INV, inviteId);
    const pSnap = await tx.get(pRef);
    const iSnap = await tx.get(iRef);
    if (!(pSnap.exists && pSnap.exists())) throw fail('PARTICIPANT_NOT_FOUND', 'Participant not found.');
    const cur = pSnap.data();
    if (str(cur.organizationId) !== org) throw fail('CLAIM_DENIED', 'Cross-organisation claim denied.');
    if (str(cur.authUserId)) throw fail('CLAIM_ALREADY_LINKED', 'This participant is already linked to an account.');
    if (!(iSnap.exists && iSnap.exists())) throw fail('CLAIM_NO_INVITE', 'No invitation matches your account for this participant.');
    const inv = iSnap.data();
    if (str(inv.status) !== 'pending') throw fail('CLAIM_INVITE_CONSUMED', 'This invitation has already been used. Ask for a fresh invitation.');
    if (str(inv.organizationId) !== org || str(inv.participantId) !== pid) throw fail('CLAIM_INVITE_MISMATCH', 'The invitation does not match this participant.');
    // Pass 85F.3 — the invite must be from the CURRENT account-authorization epoch. A pending invite issued before the last
    // operator unlink (a stale sibling / old-generation invite) can NEVER authorize a claim without a fresh operator invite.
    const pGen = Number.isInteger(+cur.accountLinkGeneration) ? Math.floor(+cur.accountLinkGeneration) : 0;
    const iGen = Number.isInteger(+inv.accountLinkGeneration) ? Math.floor(+inv.accountLinkGeneration) : 0;
    if (iGen !== pGen) throw fail('CLAIM_INVITE_STALE', 'This invitation is no longer valid. Ask for a fresh invitation.');
    // Atomic: link the profile AND consume the invite together (generation is NOT changed by a claim).
    tx.set(pRef, { authUserId: uid, updatedAt: serverTimestamp() }, { merge: true });
    tx.set(iRef, { status: 'accepted', acceptedByAuthUserId: uid, updatedAt: serverTimestamp() }, { merge: true });
  });
  return { participantId: pid, linked: true };
}
